// Service-Specific Dynamic Flow Configuration
// Each service has its own unique set of steps with field definitions

export type FieldType = 'select' | 'multi-select' | 'text' | 'number' | 'radio' | 'checkbox' | 'file' | 'conditional';

export interface FieldOption {
  id: string;
  label: string;
}

export interface FormField {
  id: string;
  label: string;
  type: FieldType;
  options?: FieldOption[];
  placeholder?: string;
  required?: boolean;
  unit?: string;
  conditionalOn?: { field: string; value: string };
  subFields?: FormField[];
  helpText?: string;
}

export interface ServiceStep {
  title: string;
  description?: string;
  fields: FormField[];
}

export interface ServiceFlow {
  id: string;
  label: string;
  icon: string;
  steps: ServiceStep[];
}

// ─── 1. SURFACE WELL TESTING ──────────────────────────────────────────────────
const surfaceWellTesting: ServiceFlow = {
  id: 'surface-well-testing',
  label: 'Surface Well Testing',
  icon: '🔬',
  steps: [
    {
      title: 'Well Information',
      description: 'Define well type and pressure specifications',
      fields: [
        { id: 'wellType', label: 'Well Type', type: 'select', required: true, options: [
          { id: 'oil', label: 'Oil' }, { id: 'gas', label: 'Gas' },
          { id: 'condensate', label: 'Condensate' }, { id: 'water-injection', label: 'Water Injection' }
        ]},
        { id: 'wellheadPressureRating', label: 'Wellhead Pressure Rating', type: 'select', required: true, options: [
          { id: '1440', label: '1,440 PSI' }, { id: '3000', label: '3,000 PSI' },
          { id: '5000', label: '5,000 PSI' }, { id: '10000', label: '10,000 PSI' }
        ]},
        { id: 'expectedFlowRate', label: 'Expected Flow Rate', type: 'number', required: true, placeholder: 'Enter value' },
        { id: 'flowRateUnit', label: 'Flow Rate Unit', type: 'select', required: true, options: [
          { id: 'bpd', label: 'BPD' }, { id: 'mmscfd', label: 'MMSCFD' }
        ]},
      ]
    },
    {
      title: 'Safety & Duration',
      description: 'H₂S presence and test duration',
      fields: [
        { id: 'h2sPresent', label: 'H₂S Present?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'h2sSafetyChecklist', label: 'H₂S Safety Equipment Required', type: 'multi-select',
          conditionalOn: { field: 'h2sPresent', value: 'yes' },
          options: [
            { id: 'h2s-monitors', label: 'H₂S Personal Monitors' },
            { id: 'scba', label: 'SCBA Units' },
            { id: 'wind-socks', label: 'Wind Socks & Direction Indicators' },
            { id: 'cascade-system', label: 'Cascade Breathing Air System' },
            { id: 'h2s-alarm', label: 'Fixed H₂S Alarm System' },
            { id: 'evacuation-plan', label: 'Evacuation Route Markers' },
          ]
        },
        { id: 'testDuration', label: 'Test Duration', type: 'number', required: true, unit: 'days', placeholder: 'Number of days' },
      ]
    },
    {
      title: 'Equipment Selection',
      description: 'Select required equipment for the test',
      fields: [
        { id: 'equipment', label: 'Equipment Required', type: 'multi-select', required: true, options: [
          { id: 'choke-manifold', label: 'Choke Manifold' },
          { id: 'ssv-esd', label: 'SSV with ESD' },
          { id: 'separator-3phase', label: 'Test Separator (3-Phase)' },
          { id: 'separator-4phase', label: 'Test Separator (4-Phase)' },
          { id: 'surge-tank', label: 'Surge Tank' },
          { id: 'desander', label: 'Desander' },
          { id: 'sand-filter', label: 'Sand Filter' },
          { id: 'burner-head', label: 'Burner Head' },
          { id: 'data-acquisition', label: 'Data Acquisition Unit' },
          { id: 'pipe-racks', label: 'Pipe Racks' },
        ]}
      ]
    },
    {
      title: 'Crew & Location',
      description: 'Crew requirements and mobilization details',
      fields: [
        { id: 'crewRequired', label: 'Crew Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'crewRoles', label: 'Crew Roles', type: 'multi-select',
          conditionalOn: { field: 'crewRequired', value: 'yes' },
          options: [
            { id: 'wt-supervisor', label: 'Well Test Supervisor' },
            { id: 'operators', label: 'Operators' },
            { id: 'hse-officer', label: 'HSE Officer' },
          ]
        },
        { id: 'fieldName', label: 'Field Name', type: 'text', required: true, placeholder: 'Enter field name' },
        { id: 'region', label: 'Region', type: 'text', required: true, placeholder: 'Enter region' },
      ]
    }
  ]
};

// ─── 2. PRESSURE CONTROL EQUIPMENT ──────────────────────────────────────────
const pressureControl: ServiceFlow = {
  id: 'pressure-control',
  label: 'Pressure Control Equipment',
  icon: '⚡',
  steps: [
    {
      title: 'Service Details',
      description: 'Define service type and pressure requirements',
      fields: [
        { id: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
          { id: 'wellhead-installation', label: 'Wellhead Installation' },
          { id: 'bop-maintenance', label: 'BOP Maintenance' },
          { id: 'flowhead', label: 'Flowhead' },
          { id: 'gate-valve-replacement', label: 'Gate Valve Replacement' },
        ]},
        { id: 'pressureRating', label: 'Pressure Rating Required', type: 'select', required: true, options: [
          { id: '10k', label: 'Up to 10,000 PSI' },
          { id: '15k', label: 'Up to 15,000 PSI' },
          { id: '20k', label: 'Up to 20,000 PSI' },
        ]},
        { id: 'boreSize', label: 'Bore Size', type: 'text', required: true, placeholder: 'e.g., 2-1/16", 3-1/8"' },
        { id: 'h2sSour', label: 'H₂S / Sour Service?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    },
    {
      title: 'Equipment & Certification',
      description: 'Select equipment and certification requirements',
      fields: [
        { id: 'equipment', label: 'Equipment Required', type: 'multi-select', required: true, options: [
          { id: 'gate-valves', label: 'Gate Valves' },
          { id: 'ball-valves', label: 'Ball Valves' },
          { id: 'plug-valves', label: 'Plug Valves' },
          { id: 'bpv', label: 'BPV' },
          { id: 'chokes', label: 'Chokes' },
          { id: 'flowheads', label: 'Flowheads' },
          { id: 'crosses', label: 'Crosses' },
          { id: 'tees', label: 'Tees' },
        ]},
        { id: 'certification', label: 'Certification Requirement', type: 'select', required: true, options: [
          { id: 'api-6a', label: 'API 6A' },
          { id: 'api-6d', label: 'API 6D' },
          { id: 'both', label: 'Both API 6A & 6D' },
        ]},
      ]
    },
    {
      title: 'Crew Requirements',
      fields: [
        { id: 'crewRequired', label: 'Crew Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'crewRoles', label: 'Crew Roles', type: 'multi-select',
          conditionalOn: { field: 'crewRequired', value: 'yes' },
          options: [
            { id: 'pc-technician', label: 'Pressure Control Technician' },
            { id: 'supervisor', label: 'Supervisor' },
          ]
        },
      ]
    }
  ]
};

// ─── 3. STIMULATION & NITROGEN ──────────────────────────────────────────────
const stimulationNitrogen: ServiceFlow = {
  id: 'stimulation-nitrogen',
  label: 'Stimulation & Nitrogen',
  icon: '💨',
  steps: [
    {
      title: 'Service Specifications',
      description: 'Define stimulation or nitrogen service requirements',
      fields: [
        { id: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
          { id: 'nitrogen-purging', label: 'Nitrogen Purging' },
          { id: 'nitrogen-lifting', label: 'Nitrogen Lifting' },
          { id: 'acid-stimulation', label: 'Acid Stimulation' },
          { id: 'hydraulic-fracturing', label: 'Hydraulic Fracturing Support' },
          { id: 'coil-tubing-assist', label: 'Coil Tubing Assist' },
        ]},
        { id: 'nitrogenVolume', label: 'Nitrogen Volume Required', type: 'number', required: true, placeholder: 'Enter volume' },
        { id: 'nitrogenVolumeUnit', label: 'Volume Unit', type: 'select', required: true, options: [
          { id: 'scf', label: 'SCF' }, { id: 'm3', label: 'm³' }
        ]},
        { id: 'pumpRate', label: 'Pump Rate Required', type: 'number', required: true, unit: 'BPM', placeholder: 'BPM' },
        { id: 'maxTreatingPressure', label: 'Max Treating Pressure', type: 'number', required: true, unit: 'PSI', placeholder: 'PSI' },
      ]
    },
    {
      title: 'Equipment Selection',
      fields: [
        { id: 'equipment', label: 'Equipment Required', type: 'multi-select', required: true, options: [
          { id: 'single-pump-skid', label: 'Single Pump Skid' },
          { id: 'twin-pump-trailer', label: 'Twin Pump Trailer' },
          { id: 'nitrogen-pump', label: 'Nitrogen Pump' },
          { id: 'iso-tank', label: 'ISO Tank' },
          { id: 'bulk-tanker', label: 'Bulk Tanker' },
          { id: 'batch-mixer', label: 'Batch Mixer' },
          { id: 'mixing-tank', label: 'Mixing Tank' },
          { id: 'frac-tank-500', label: 'Frac Tank (500 BBL)' },
          { id: 'transfer-pump', label: 'Transfer Pump' },
          { id: 'backside-pump', label: 'Backside Pump' },
        ]},
        { id: 'chemicalRequired', label: 'Chemical Package Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ], helpText: 'If yes, a Chemicals Supply quote will also be initiated' },
      ]
    },
    {
      title: 'Crew Requirements',
      fields: [
        { id: 'crewRoles', label: 'Crew Required', type: 'multi-select', options: [
          { id: 'stim-supervisor', label: 'Stimulation Supervisor' },
          { id: 'nitrogen-operator', label: 'Nitrogen Operator' },
          { id: 'pump-operator', label: 'Pump Operator' },
        ]},
      ]
    }
  ]
};

// ─── 4. PUMPING & HYDROTESTING ──────────────────────────────────────────────
const pumpingHydrotesting: ServiceFlow = {
  id: 'pumping-hydrotesting',
  label: 'Pumping & Hydrotesting',
  icon: '⚙️',
  steps: [
    {
      title: 'Test Specifications',
      description: 'Define hydrostatic test requirements',
      fields: [
        { id: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
          { id: 'hydrostatic-test', label: 'Hydrostatic Test' },
          { id: 'pipeline-flushing', label: 'Pipeline Flushing' },
          { id: 'pressure-test', label: 'Pressure Test' },
          { id: 'leak-test', label: 'Leak Test' },
        ]},
        { id: 'testPressure', label: 'Test Pressure Required', type: 'number', required: true, unit: 'PSI', placeholder: 'PSI' },
        { id: 'fluidVolume', label: 'Volume of Fluid', type: 'number', required: true, placeholder: 'Enter volume' },
        { id: 'fluidVolumeUnit', label: 'Volume Unit', type: 'select', required: true, options: [
          { id: 'bbl', label: 'BBL' }, { id: 'm3', label: 'm³' }
        ]},
        { id: 'fluidType', label: 'Fluid Type', type: 'select', required: true, options: [
          { id: 'water', label: 'Water' },
          { id: 'chemical', label: 'Chemical' },
          { id: 'inhibited-water', label: 'Inhibited Water' },
        ]},
      ]
    },
    {
      title: 'Equipment & Certification',
      fields: [
        { id: 'equipment', label: 'Equipment Required', type: 'multi-select', required: true, options: [
          { id: 'hp-pump-unit', label: 'High-Pressure Pump Unit' },
          { id: 'test-manifold', label: 'Test Manifold' },
          { id: 'chart-recorder', label: 'Chart Recorder' },
          { id: 'relief-valve', label: 'Relief Valve' },
          { id: 'calibrated-gauges', label: 'Calibrated Gauges' },
          { id: 'frac-tanks', label: 'Frac Tanks' },
        ]},
        { id: 'certWitness', label: 'Test Certification / Witness Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    },
    {
      title: 'Crew Requirements',
      fields: [
        { id: 'crewRoles', label: 'Crew Required', type: 'multi-select', options: [
          { id: 'test-supervisor', label: 'Test Supervisor' },
          { id: 'pump-operator', label: 'Pump Operator' },
        ]},
      ]
    }
  ]
};

// ─── 5. EQUIPMENT RENTAL ──────────────────────────────────────────────────────
const equipmentRental: ServiceFlow = {
  id: 'equipment-rental',
  label: 'Equipment Rental',
  icon: '🏗️',
  steps: [
    {
      title: 'Equipment Category',
      description: 'Select equipment category and specifications',
      fields: [
        { id: 'equipmentCategory', label: 'Equipment Category', type: 'select', required: true, options: [
          { id: 'power', label: 'Power' },
          { id: 'lifting', label: 'Lifting' },
          { id: 'access', label: 'Access' },
          { id: 'tanks', label: 'Tanks' },
          { id: 'lighting', label: 'Lighting' },
          { id: 'compression', label: 'Compression' },
        ]},
        // Sub-selectors per category
        { id: 'powerSize', label: 'Generator Size', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'power' },
          options: [
            { id: '17kva', label: '17 KVA' }, { id: '50kva', label: '50 KVA' },
            { id: '100kva', label: '100 KVA' }, { id: '200kva+', label: '200 KVA+' },
          ]},
        { id: 'liftingType', label: 'Lifting Equipment', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'lifting' },
          options: [
            { id: 'crane-25t', label: 'Crane (25 Ton)' }, { id: 'crane-50t', label: 'Crane (50 Ton)' },
            { id: 'crane-100t', label: 'Crane (100 Ton)' }, { id: 'crane-200t+', label: 'Crane (200+ Ton)' },
            { id: 'forklift-3t', label: 'Forklift (3 Ton)' }, { id: 'forklift-5t', label: 'Forklift (5 Ton)' },
            { id: 'forklift-10t', label: 'Forklift (10 Ton)' },
          ]},
        { id: 'accessType', label: 'Access Equipment', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'access' },
          options: [
            { id: 'scissor-lift', label: 'Scissor Lift' },
            { id: 'telescopic-12m', label: 'Telescopic Man Lift (12m)' },
            { id: 'telescopic-18m', label: 'Telescopic Man Lift (18m)' },
            { id: 'telescopic-24m', label: 'Telescopic Man Lift (24m)' },
          ]},
        { id: 'tankType', label: 'Tank Type', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'tanks' },
          options: [
            { id: 'frac-coated', label: '500 BBL Frac Tank (Coated)' },
            { id: 'frac-non-coated', label: '500 BBL Frac Tank (Non-Coated)' },
            { id: 'pressurized-offshore', label: 'Pressurized Offshore Tank' },
          ]},
        { id: 'lightingType', label: 'Lighting Type', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'lighting' },
          options: [
            { id: 'diesel-tower', label: 'Diesel Tower Light' },
            { id: 'solar-tower', label: 'Solar Tower Light' },
          ]},
        { id: 'compressionType', label: 'Air Compressor', type: 'select',
          conditionalOn: { field: 'equipmentCategory', value: 'compression' },
          options: [
            { id: '185cfm', label: '185 CFM' }, { id: '375cfm', label: '375 CFM' },
            { id: '750cfm', label: '750 CFM' }, { id: '1200cfm', label: '1,200 CFM' },
          ]},
      ]
    },
    {
      title: 'Rental Details',
      fields: [
        { id: 'rentalDuration', label: 'Rental Duration', type: 'number', required: true, placeholder: 'Enter duration' },
        { id: 'rentalDurationUnit', label: 'Duration Unit', type: 'select', required: true, options: [
          { id: 'days', label: 'Days' }, { id: 'weeks', label: 'Weeks' }, { id: 'months', label: 'Months' }
        ]},
        { id: 'withOperator', label: 'With Operator?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'deliveryLocation', label: 'Delivery Location', type: 'text', required: true, placeholder: 'Enter delivery location' },
      ]
    }
  ]
};

// ─── 6. CHEMICALS SUPPLY ──────────────────────────────────────────────────────
const chemicalsSupply: ServiceFlow = {
  id: 'chemicals-supply',
  label: 'Chemicals Supply',
  icon: '🧪',
  steps: [
    {
      title: 'Application & Chemical Type',
      description: 'Define chemical application and type',
      fields: [
        { id: 'application', label: 'Application', type: 'select', required: true, options: [
          { id: 'production', label: 'Production' },
          { id: 'drilling', label: 'Drilling' },
          { id: 'water-treatment', label: 'Water Treatment' },
          { id: 'stimulation', label: 'Stimulation' },
          { id: 'pipeline', label: 'Pipeline' },
        ]},
        { id: 'chemicalType', label: 'Chemical Type', type: 'multi-select', required: true, options: [
          { id: 'demulsifier', label: 'Demulsifier' },
          { id: 'corrosion-inhibitor', label: 'Corrosion Inhibitor' },
          { id: 'scale-inhibitor', label: 'Scale Inhibitor' },
          { id: 'biocide', label: 'Biocide' },
          { id: 'ph-adjuster', label: 'pH Adjuster' },
          { id: 'antifoam', label: 'Antifoam' },
          { id: 'flocculant', label: 'Flocculant' },
          { id: 'polymer', label: 'Polymer' },
        ]},
      ]
    },
    {
      title: 'Volume & Dosing',
      fields: [
        { id: 'volumeRequired', label: 'Volume Required', type: 'number', required: true, placeholder: 'Enter quantity' },
        { id: 'volumeUnit', label: 'Volume Unit', type: 'select', required: true, options: [
          { id: 'liters', label: 'Liters' }, { id: 'drums', label: 'Drums' },
          { id: 'ibc', label: 'IBC' }, { id: 'bulk-tanker', label: 'Bulk Tanker' },
        ]},
        { id: 'dosingRateKnown', label: 'Dosing Rate Known?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'dosingRate', label: 'Dosing Rate (ppm)', type: 'number',
          conditionalOn: { field: 'dosingRateKnown', value: 'yes' },
          placeholder: 'Enter dosing rate in ppm'
        },
      ]
    },
    {
      title: 'Delivery & Documentation',
      fields: [
        { id: 'msdsRequired', label: 'MSDS Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'deliverySchedule', label: 'Delivery Schedule', type: 'select', required: true, options: [
          { id: 'one-time', label: 'One-Time' }, { id: 'recurring', label: 'Recurring Monthly' }
        ]},
        { id: 'techDataSheet', label: 'Technical Data Sheet Required Before Order?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    }
  ]
};

// ─── 7. LOGISTICS & TRANSPORTATION ──────────────────────────────────────────
const logisticsTransport: ServiceFlow = {
  id: 'logistics-transport',
  label: 'Logistics & Transportation',
  icon: '🚛',
  steps: [
    {
      title: 'Service & Route',
      description: 'Define transport service and route details',
      fields: [
        { id: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
          { id: 'heavy-lift', label: 'Heavy Lift Transport' },
          { id: 'equipment-mob', label: 'Equipment Mobilization' },
          { id: 'dangerous-goods', label: 'Dangerous Goods' },
          { id: 'personnel', label: 'Personnel Transport' },
          { id: 'crane-truck', label: 'Crane Truck' },
        ]},
        { id: 'originLocation', label: 'Origin Location', type: 'text', required: true, placeholder: 'Enter origin' },
        { id: 'destinationLocation', label: 'Destination Location', type: 'text', required: true, placeholder: 'Enter destination' },
      ]
    },
    {
      title: 'Cargo Details',
      fields: [
        { id: 'cargoDescription', label: 'Cargo Description', type: 'text', required: true, placeholder: 'Describe the cargo' },
        { id: 'estimatedWeight', label: 'Estimated Weight', type: 'number', required: true, unit: 'tons', placeholder: 'Tons' },
        { id: 'dimensions', label: 'Dimensions (L x W x H)', type: 'text', placeholder: 'e.g., 12m x 3m x 4m' },
        { id: 'specialHandling', label: 'Special Handling', type: 'multi-select', options: [
          { id: 'hazmat', label: 'Hazmat' },
          { id: 'oversized', label: 'Oversized' },
          { id: 'fragile', label: 'Fragile' },
        ]},
      ]
    },
    {
      title: 'Vehicle & Urgency',
      fields: [
        { id: 'vehicleType', label: 'Vehicle Type', type: 'multi-select', required: true, options: [
          { id: 'tractor-lowbed', label: 'Tractor Head + Lowbed' },
          { id: 'flatbed', label: 'Flatbed' },
          { id: 'crane-truck', label: 'Crane Truck' },
          { id: 'water-tanker', label: 'Water Tanker' },
          { id: 'diesel-tanker', label: 'Diesel Tanker' },
          { id: 'light-vehicles', label: 'Light Vehicles' },
        ]},
        { id: 'customsClearance', label: 'Customs Clearance Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'urgency', label: 'Urgency', type: 'select', required: true, options: [
          { id: 'standard', label: 'Standard' },
          { id: 'express', label: 'Express' },
          { id: 'emergency', label: 'Emergency' },
        ]},
      ]
    }
  ]
};

// ─── 8. FABRICATION & SKID ASSEMBLY ──────────────────────────────────────────
const fabricationSkid: ServiceFlow = {
  id: 'fabrication-skid',
  label: 'Fabrication & Skid Assembly',
  icon: '🏭',
  steps: [
    {
      title: 'Fabrication Type & Specifications',
      fields: [
        { id: 'fabricationType', label: 'Type', type: 'select', required: true, options: [
          { id: 'new', label: 'New Fabrication' },
          { id: 'modification', label: 'Modification' },
          { id: 'repair', label: 'Repair' },
          { id: 're-certification', label: 'Re-certification' },
        ]},
        { id: 'skidType', label: 'Skid Type', type: 'select', required: true, options: [
          { id: 'process', label: 'Process Skid' },
          { id: 'pump', label: 'Pump Skid' },
          { id: 'chemical-injection', label: 'Chemical Injection Skid' },
          { id: 'utility', label: 'Utility Skid' },
        ]},
        { id: 'materialSpec', label: 'Material Specification', type: 'select', required: true, options: [
          { id: 'carbon-steel', label: 'Carbon Steel' },
          { id: 'ss-316', label: 'SS 316' },
          { id: 'duplex', label: 'Duplex' },
        ]},
        { id: 'designStandard', label: 'Design Standard', type: 'select', required: true, options: [
          { id: 'asme', label: 'ASME' },
          { id: 'api', label: 'API' },
          { id: 'client-spec', label: 'Client Specification' },
        ]},
      ]
    },
    {
      title: 'Dimensions & Surface Treatment',
      fields: [
        { id: 'dimensions', label: 'Dimensions (L x W x H in meters)', type: 'text', required: true, placeholder: 'e.g., 6 x 2.5 x 3' },
        { id: 'weightEstimate', label: 'Weight Estimate', type: 'text', placeholder: 'e.g., 5 tons' },
        { id: 'surfaceTreatment', label: 'Surface Treatment', type: 'select', required: true, options: [
          { id: 'painting', label: 'Painting Spec' },
          { id: 'hot-dip', label: 'Hot Dip Galvanizing' },
          { id: 'bare', label: 'Bare' },
        ]},
      ]
    },
    {
      title: 'Inspection & Delivery',
      fields: [
        { id: 'tpiRequired', label: 'Third-Party Inspection Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'tpiBody', label: 'TPI Body', type: 'text',
          conditionalOn: { field: 'tpiRequired', value: 'yes' },
          placeholder: 'e.g., TÜV, Bureau Veritas, SGS'
        },
        { id: 'deliveryTimeline', label: 'Delivery Timeline', type: 'text', required: true, placeholder: 'e.g., 8 weeks from PO' },
      ]
    }
  ]
};

// ─── 9. ENGINEERING SERVICES ──────────────────────────────────────────────────
const engineeringServices: ServiceFlow = {
  id: 'engineering-services',
  label: 'Engineering Services',
  icon: '📐',
  steps: [
    {
      title: 'Discipline & Deliverables',
      fields: [
        { id: 'discipline', label: 'Discipline', type: 'select', required: true, options: [
          { id: 'process', label: 'Process' },
          { id: 'mechanical', label: 'Mechanical' },
          { id: 'piping', label: 'Piping' },
          { id: 'instrumentation', label: 'Instrumentation' },
          { id: 'structural', label: 'Structural' },
        ]},
        { id: 'deliverableType', label: 'Deliverable Type', type: 'multi-select', required: true, options: [
          { id: 'eng-study', label: 'Engineering Study' },
          { id: 'pid', label: 'P&ID' },
          { id: 'datasheet', label: 'Equipment Datasheet' },
          { id: 'as-built', label: 'As-Built' },
          { id: 'calc-package', label: 'Calculation Package' },
          { id: 'inspection-report', label: 'Inspection Report' },
        ]},
      ]
    },
    {
      title: 'Project Phase & Tools',
      fields: [
        { id: 'projectPhase', label: 'Project Phase', type: 'select', required: true, options: [
          { id: 'feed', label: 'FEED' },
          { id: 'detailed', label: 'Detailed Engineering' },
          { id: 'construction', label: 'Construction Support' },
          { id: 'commissioning', label: 'Commissioning' },
        ]},
        { id: 'softwareRequired', label: 'Software Required', type: 'multi-select', options: [
          { id: 'autocad', label: 'AutoCAD' },
          { id: 'pdms', label: 'PDMS' },
          { id: 'caesar-ii', label: 'Caesar II' },
          { id: 'pvelite', label: 'PVElite' },
          { id: 'hysys', label: 'HYSYS' },
        ]},
        { id: 'manhourEstimate', label: 'Manhour Estimate Needed?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'duration', label: 'Duration', type: 'text', required: true, placeholder: 'e.g., 4 weeks' },
      ]
    }
  ]
};

// ─── 10. AI SYSTEMS & AUTOMATION ──────────────────────────────────────────────
const aiSystems: ServiceFlow = {
  id: 'ai-systems',
  label: 'AI Systems & Automation / Well Site Surveillance',
  icon: '🤖',
  steps: [
    {
      title: 'Surveillance Requirements',
      fields: [
        { id: 'surveillanceType', label: 'Surveillance Type', type: 'select', required: true, options: [
          { id: 'remote-monitoring', label: 'Remote Monitoring' },
          { id: 'ai-analytics', label: 'AI Analytics' },
          { id: 'camera-system', label: 'Camera System' },
          { id: 'alarm-system', label: 'Alarm System' },
          { id: 'scada-integration', label: 'SCADA Integration' },
        ]},
        { id: 'numWellSites', label: 'Number of Well Sites', type: 'number', required: true, placeholder: 'Enter number' },
        { id: 'connectivity', label: 'Connectivity Available on Site?', type: 'select', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }, { id: 'satellite', label: 'Satellite' }
        ]},
      ]
    },
    {
      title: 'Data & Integration',
      fields: [
        { id: 'dataFrequency', label: 'Data Frequency', type: 'select', required: true, options: [
          { id: 'realtime', label: 'Real-Time' },
          { id: 'hourly', label: 'Hourly' },
          { id: 'daily', label: 'Daily' },
        ]},
        { id: 'existingIntegration', label: 'Integration with Existing System?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'existingSystem', label: 'Specify System', type: 'text',
          conditionalOn: { field: 'existingIntegration', value: 'yes' },
          placeholder: 'Enter system name/type'
        },
        { id: 'reportFormat', label: 'Report Format Required', type: 'multi-select', required: true, options: [
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'pdf', label: 'PDF' },
          { id: 'email-alerts', label: 'Email Alerts' },
        ]},
        { id: 'serviceDuration', label: 'Duration of Service', type: 'number', required: true, unit: 'months', placeholder: 'Months' },
      ]
    }
  ]
};

// ─── 11. SPARE PARTS & CONSUMABLES ──────────────────────────────────────────
const spareParts: ServiceFlow = {
  id: 'spare-parts',
  label: 'Spare Parts & Consumables',
  icon: '🔧',
  steps: [
    {
      title: 'Equipment & Parts',
      fields: [
        { id: 'equipmentBrand', label: 'Equipment Brand/Model', type: 'text', required: true, placeholder: 'Enter brand/model or part numbers' },
        { id: 'partCategory', label: 'Part Category', type: 'multi-select', required: true, options: [
          { id: 'valves', label: 'Valves' },
          { id: 'seals', label: 'Seals' },
          { id: 'gaskets', label: 'Gaskets' },
          { id: 'fittings', label: 'Fittings' },
          { id: 'instrumentation', label: 'Instrumentation' },
          { id: 'pumps', label: 'Pumps' },
          { id: 'filters', label: 'Filters' },
        ]},
        { id: 'quantity', label: 'Quantity Per Item', type: 'text', required: true, placeholder: 'Enter quantities' },
      ]
    },
    {
      title: 'Urgency & Certification',
      fields: [
        { id: 'urgency', label: 'Urgency', type: 'select', required: true, options: [
          { id: 'standard', label: 'Standard Delivery' },
          { id: 'express', label: 'Express' },
          { id: 'emergency-aog', label: 'Emergency (AOG-Style)' },
        ]},
        { id: 'materialCerts', label: 'Material Certifications Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'certType', label: 'Certification Type', type: 'multi-select',
          conditionalOn: { field: 'materialCerts', value: 'yes' },
          options: [
            { id: 'mtr', label: 'MTR' },
            { id: 'coc', label: 'CoC' },
          ]
        },
        { id: 'supplierPref', label: 'Preferred Supplier', type: 'select', required: true, options: [
          { id: 'oem', label: 'OEM' },
          { id: 'equivalent', label: 'Equivalent' },
        ]},
        { id: 'uploadRfq', label: 'Upload RFQ Document?', type: 'radio', options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    }
  ]
};

// ─── 12. SOLID CONTROL & WASTE MANAGEMENT ───────────────────────────────────
const solidControl: ServiceFlow = {
  id: 'solid-control',
  label: 'Solid Control & Waste Management',
  icon: '♻️',
  steps: [
    {
      title: 'Waste Details',
      fields: [
        { id: 'serviceType', label: 'Service Type', type: 'select', required: true, options: [
          { id: 'drilling-waste', label: 'Drilling Waste' },
          { id: 'produced-water', label: 'Produced Water Treatment' },
          { id: 'soil-remediation', label: 'Soil Remediation' },
          { id: 'cuttings-disposal', label: 'Cuttings Disposal' },
        ]},
        { id: 'wasteVolume', label: 'Volume of Waste', type: 'number', required: true, unit: 'm³/day', placeholder: 'm³ per day' },
        { id: 'wasteClassification', label: 'Waste Classification', type: 'select', required: true, options: [
          { id: 'hazardous', label: 'Hazardous' },
          { id: 'non-hazardous', label: 'Non-Hazardous' },
        ]},
      ]
    },
    {
      title: 'Equipment & Disposal',
      fields: [
        { id: 'equipment', label: 'Equipment Required', type: 'multi-select', options: [
          { id: 'mud-cooling', label: 'Mud Cooling System' },
          { id: 'portable-water', label: 'Portable Water Treatment' },
          { id: 'waste-skips', label: 'Waste Skips' },
        ]},
        { id: 'disposalMethod', label: 'Disposal Method', type: 'select', required: true, options: [
          { id: 'on-site', label: 'On-Site Treatment' },
          { id: 'off-site', label: 'Off-Site Licensed Disposal' },
        ]},
        { id: 'envPermit', label: 'Environmental Permit Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'regulatoryBody', label: 'Regulatory Body', type: 'select', required: true, options: [
          { id: 'ncec-ksa', label: 'NCEC Saudi Arabia' },
          { id: 'esma-uae', label: 'ESMA UAE' },
          { id: 'client-ehs', label: 'Client EHS' },
        ]},
      ]
    }
  ]
};

// ─── 13. DOWNHOLE FIBER OPTICS ──────────────────────────────────────────────
const downholeFiber: ServiceFlow = {
  id: 'downhole-fiber',
  label: 'Downhole Fiber Optics',
  icon: '📡',
  steps: [
    {
      title: 'Application & Well Details',
      fields: [
        { id: 'application', label: 'Application', type: 'select', required: true, options: [
          { id: 'dts', label: 'DTS (Distributed Temperature Sensing)' },
          { id: 'das', label: 'DAS (Distributed Acoustic Sensing)' },
          { id: 'strain', label: 'Strain Sensing' },
          { id: 'permanent', label: 'Permanent Monitoring' },
        ]},
        { id: 'wellDepth', label: 'Well Depth', type: 'number', required: true, unit: 'meters', placeholder: 'Meters' },
        { id: 'temperatureRating', label: 'Temperature Rating Required', type: 'number', required: true, unit: '°C', placeholder: '°C' },
        { id: 'existingCompletion', label: 'Existing Completion Type?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    },
    {
      title: 'Data & Duration',
      fields: [
        { id: 'dataAcquisition', label: 'Data Acquisition at Surface Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'installationType', label: 'Installation Duration', type: 'select', required: true, options: [
          { id: 'permanent', label: 'Permanent Installation' },
          { id: 'temporary', label: 'Temporary' },
        ]},
        { id: 'reportDeliverables', label: 'Report Deliverables', type: 'text', required: true, placeholder: 'Describe required reports' },
      ]
    }
  ]
};

// ─── 14. PUMPING SOLUTIONS ──────────────────────────────────────────────────
const pumpingSolutions: ServiceFlow = {
  id: 'pumping-solutions',
  label: 'Pumping Solutions',
  icon: '🔄',
  steps: [
    {
      title: 'Pump Specifications',
      fields: [
        { id: 'pumpType', label: 'Pump Type', type: 'select', required: true, options: [
          { id: 'centrifugal', label: 'Centrifugal' },
          { id: 'reciprocating', label: 'Reciprocating' },
          { id: 'submersible', label: 'Submersible' },
          { id: 'progressive-cavity', label: 'Progressive Cavity' },
        ]},
        { id: 'fluidType', label: 'Fluid Type', type: 'select', required: true, options: [
          { id: 'water', label: 'Water' },
          { id: 'oil', label: 'Oil' },
          { id: 'chemical', label: 'Chemical' },
          { id: 'slurry', label: 'Slurry' },
        ]},
        { id: 'flowRate', label: 'Flow Rate', type: 'number', required: true, placeholder: 'Enter flow rate' },
        { id: 'flowRateUnit', label: 'Flow Rate Unit', type: 'select', required: true, options: [
          { id: 'm3hr', label: 'm³/hr' }, { id: 'bpd', label: 'BPD' }
        ]},
        { id: 'dischargePressure', label: 'Discharge Pressure', type: 'number', required: true, placeholder: 'Enter pressure' },
        { id: 'pressureUnit', label: 'Pressure Unit', type: 'select', required: true, options: [
          { id: 'bar', label: 'Bar' }, { id: 'psi', label: 'PSI' }
        ]},
      ]
    },
    {
      title: 'Configuration & Crew',
      fields: [
        { id: 'powerSource', label: 'Power Source', type: 'select', required: true, options: [
          { id: 'diesel', label: 'Diesel' }, { id: 'electric', label: 'Electric' }
        ]},
        { id: 'mounting', label: 'Mounting', type: 'select', required: true, options: [
          { id: 'skid', label: 'Skid-Mounted' }, { id: 'trailer', label: 'Trailer-Mounted' }
        ]},
        { id: 'rentalOrPurchase', label: 'Rental or Purchase?', type: 'select', required: true, options: [
          { id: 'rental', label: 'Rental' }, { id: 'purchase', label: 'Purchase' }
        ]},
        { id: 'crewWithPump', label: 'Crew with Pump?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    }
  ]
};

// ─── 15. FLAME DETECTION SYSTEMS ──────────────────────────────────────────────
const flameDetection: ServiceFlow = {
  id: 'flame-detection',
  label: 'Flame Detection Systems',
  icon: '🔥',
  steps: [
    {
      title: 'Application & Detection',
      fields: [
        { id: 'application', label: 'Application', type: 'select', required: true, options: [
          { id: 'well-site', label: 'Well Site' },
          { id: 'flare-stack', label: 'Flare Stack' },
          { id: 'process-area', label: 'Process Area' },
          { id: 'offshore-platform', label: 'Offshore Platform' },
        ]},
        { id: 'detectionType', label: 'Detection Type', type: 'select', required: true, options: [
          { id: 'uv-ir', label: 'UV/IR' },
          { id: 'cctv-thermal', label: 'CCTV Thermal' },
          { id: 'multi-spectrum', label: 'Multi-Spectrum' },
        ]},
        { id: 'detectionPoints', label: 'Number of Detection Points', type: 'number', required: true, placeholder: 'Enter number' },
        { id: 'areaClassification', label: 'Area Classification', type: 'select', required: true, options: [
          { id: 'zone-1', label: 'Zone 1' }, { id: 'zone-2', label: 'Zone 2' }
        ]},
      ]
    },
    {
      title: 'Integration & Certification',
      fields: [
        { id: 'esdIntegration', label: 'Integration with ESD?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
        { id: 'certification', label: 'Certification Required', type: 'multi-select', required: true, options: [
          { id: 'atex', label: 'ATEX' },
          { id: 'iecex', label: 'IECEx' },
          { id: 'sil', label: 'SIL Rating' },
        ]},
        { id: 'commissioningRequired', label: 'Commissioning & Testing Required?', type: 'radio', required: true, options: [
          { id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }
        ]},
      ]
    }
  ]
};

// ─── ALL SERVICE FLOWS ──────────────────────────────────────────────────────
export const SERVICE_FLOWS: ServiceFlow[] = [
  surfaceWellTesting,
  pressureControl,
  stimulationNitrogen,
  pumpingHydrotesting,
  equipmentRental,
  chemicalsSupply,
  logisticsTransport,
  fabricationSkid,
  engineeringServices,
  aiSystems,
  spareParts,
  solidControl,
  downholeFiber,
  pumpingSolutions,
  flameDetection,
];

export function getServiceFlow(serviceId: string): ServiceFlow | undefined {
  return SERVICE_FLOWS.find(f => f.id === serviceId);
}
